/*
  Team load (12-tasks idea 10, wave 3) — who carries what, and how much of the work closes on time.

  Card (under the board, mode="card"): the 5 busiest people + Unassigned, each with a load bar (one block per
  open task: blue = in progress, gray = open, red = blocked or overdue), Open N, what needs attention
  ("2 late", "1 blocked") and Done N this week; on the right the team's on-time completion of the last 8 weeks
  (tasks closed on or before their due date). "This week" / "Next week" switches between everything open now
  and the tasks due next week. "Open team view" turns it into the full view (mode="full", ?view=team): every
  person, each row unfolds into their open tasks.

  Assigning: drag a board card (or, in the team view, a task row) onto a person — a card from another
  person's row moves to the new person, a board card gets the person added. Keyboard / touch: the Assign
  action on a card (⋯ › Assign people… on touch screens), or "Assign to…" (person icon) on a task row in the
  team view. A name opens the person's full history (TeamMemberTaskHistory); "Open N" shows their tasks on
  the board.
  Why not the foundation <Board>: it lays work out as columns (one per person), while this view is rows —
  name, load bar, Open, Attention, Done — next to the on-time chart, as in the approved mockup; and the cards
  being dropped live in the task lanes, not in a <Board>. So the drop target is a plain row with HTML5 drag
  and drop (mouse), and the picker above is the keyboard / touch path to the same PATCH.
  `readOnly` (BUG-002: Tasks is View only for the role, or a Viewer account): no assigning at all — nothing drags,
  no row takes a drop, no "Assign to…" on task rows, no drag hints; Unassigned shows its load bar like a person.
  Data: the board's own lists (/tasks/pending + done tasks of the last 8 weeks) — no extra endpoint.
  The metric: the approved mockup's "On-time completion · closed on or before due date". Tasks without a due
  date cannot be late, so they are left out of the rate — the plain closed counts are shown under the chart.
*/
import { useLayoutEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Columns3,
  TrendingDown,
  TrendingUp,
  UserPlus,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Segmented } from '@/components/ui/segmented';
import { DueBadge, PersonAvatar, StatusIcon } from './TaskCard';
import { AssignPanel } from './TaskQuickPanels';
import { TEAM_PERIODS, taskAssignees, taskProjects } from './taskModel';

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;
const shortDate = (iso) => formatDate(iso, { year: 'auto' });
const UNASSIGNED = 'none';
const MAX_BLOCKS = 24; // above this the bar shows three proportional segments instead of one block per task

/* ------------------------------------------------------------------------------------------------ chart */

/**
 * Weekly on-time completion bars (current week last, in navy). A week with nothing dated closed shows a short
 * dashed stub. `compact` = shorter, for the person history panel.
 */
export function OnTimeChart({ weeks, compact = false, label = 'On-time completion per week', loading = false }) {
  const height = compact ? 64 : 90;
  const summary = weeks
    .map((week) => `${shortDate(week.start)}: ${week.rate === null ? 'nothing dated closed' : `${week.rate}% (${week.onTime} of ${week.dated})`}`)
    .join('; ');
  return (
    <div className="flex flex-col" data-testid="team-ontime-chart">
      <div
        role="img"
        aria-label={loading ? `${label}: loading` : `${label}. ${summary}`}
        className="flex items-end justify-between gap-2.5 border-b border-line"
        style={{ height }}
      >
        {weeks.map((week, index) => {
          const current = index === weeks.length - 1;
          const empty = week.rate === null;
          return (
            <span
              key={week.start}
              title={`Week of ${shortDate(week.start)} · ${empty
                ? `${plural(week.closed, 'task')} closed, none with a due date`
                : `${week.onTime} of ${week.dated} on time (${week.rate}%) · ${plural(week.closed, 'task')} closed`}`}
              className={cn(
                'block min-w-[8px] max-w-[22px] flex-1 rounded-t-[4px]',
                loading && 'animate-pulse bg-[color:var(--line-2)]',
                !loading && empty && 'border border-b-0 border-dashed border-[color:var(--line-dashed)] bg-transparent',
                !loading && !empty && (current ? 'bg-brand' : 'bg-[color:var(--brand-soft-border)]')
              )}
              style={{ height: loading ? `${30 + (index % 3) * 15}%` : empty ? 6 : `${Math.max(4, week.rate)}%` }}
              data-testid={`team-ontime-bar-${index}`}
            />
          );
        })}
      </div>
      {weeks.length ? (
        <div className="mt-1.5 flex justify-between text-[10.5px] font-semibold text-ink-3" aria-hidden="true">
          <span>{shortDate(weeks[0].start)}</span>
          <span>{shortDate(weeks[weeks.length - 1].start)}</span>
        </div>
      ) : null}
    </div>
  );
}

function RatePanel({ weeks, loading, side, searching = false }) {
  const current = weeks[weeks.length - 1];
  const closed = weeks.reduce((sum, week) => sum + week.closed, 0);
  const previous = weeks[weeks.length - 2];
  const dated = weeks.reduce((sum, week) => sum + week.dated, 0);
  const onTime = weeks.reduce((sum, week) => sum + week.onTime, 0);
  // The big number is this week's (with the change since last week). Until something with a due date closes
  // this week, the whole 8 weeks stand in — and say so.
  const thisWeek = current && current.rate !== null;
  const trend = thisWeek && previous && previous.rate !== null ? current.rate - previous.rate : null;
  const overall = dated ? Math.round((onTime / dated) * 100) : null;
  return (
    <div className={cn('flex flex-col border-line-2', side ? 'border-l pl-7 pt-3' : 'mt-3 border-t pt-4')} data-testid="team-ontime">
      <div className="text-[12.5px] font-bold text-ink-2">On-time completion · last 8 weeks</div>
      <div className="mt-px text-[12px] text-ink-3">Closed on or before the due date</div>
      <div className="mt-1 flex min-h-[34px] flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
        {loading ? (
          <span className="h-7 w-16 animate-pulse rounded bg-[color:var(--line-2)]" aria-hidden="true" />
        ) : thisWeek || overall !== null ? (
          <>
            <span className="font-heading text-[28px] font-black leading-[1.1] tracking-[-0.03em] text-ink" data-testid="team-ontime-rate">
              {thisWeek ? current.rate : overall}%
            </span>
            {trend !== null ? (
              <span className={cn('inline-flex items-center gap-1 text-[12.5px] font-bold', trend >= 0 ? 'text-status-green-ink' : 'text-status-red-ink')}>
                {trend >= 0 ? <TrendingUp className="h-3.5 w-3.5" aria-hidden="true" /> : <TrendingDown className="h-3.5 w-3.5" aria-hidden="true" />}
                {trend >= 0 ? '+' : '−'}{Math.abs(trend)} pts vs last week
              </span>
            ) : (
              <span className="text-[12.5px] font-semibold text-ink-3" data-testid="team-ontime-note">
                {thisWeek
                  ? `${current.onTime} of ${current.dated} this week`
                  : `${onTime} of ${dated} over 8 weeks · none closed this week yet`}
              </span>
            )}
          </>
        ) : (
          <span className="text-[13px] font-semibold text-ink-3" data-testid="team-ontime-rate">No tasks with a due date closed in 8 weeks</span>
        )}
      </div>
      <div className={cn('mt-3.5', !side && 'max-w-[480px]')}>
        <OnTimeChart weeks={weeks} loading={loading} label="Team on-time completion per week" />
      </div>
      {loading ? null : (
        <p className="mt-2 text-[11.5px] text-ink-3" data-testid="team-ontime-closed">
          Closed: {current?.closed || 0} this week · {closed} in 8 weeks
          {closed > dated ? ` (${closed - dated} undated)` : ''}
        </p>
      )}
      {searching ? (
        // The rows follow the search; the chart follows the Person / Project / Priority / Location chips only.
        <p className="mt-1 text-[11.5px] text-ink-3" data-testid="team-ontime-search-note">The search does not narrow this chart.</p>
      ) : null}
    </div>
  );
}

/* --------------------------------------------------------------------------------------------- the rows */

function LoadBar({ row, scale }) {
  const blocks = [
    ...Array(row.progress).fill('progress'),
    ...Array(row.red).fill('red'),
    ...Array(row.plain).fill('open'),
  ];
  const fill = { progress: 'bg-status-blue', red: 'bg-status-red', open: 'bg-[color:var(--status-gray)]' };
  const parts = [
    row.progress ? `${row.progress} in progress` : '',
    row.plain ? `${row.plain} open` : '',
    row.red ? `${row.red} blocked or overdue` : '',
  ].filter(Boolean).join(', ');
  return (
    <span
      role="img"
      aria-label={row.open ? `Load: ${parts}` : 'Nothing open'}
      className="flex h-2.5 w-full items-stretch gap-[2px] overflow-hidden rounded-full bg-[color:var(--line-2)] shadow-[inset_0_0_0_1px_var(--line)]"
      data-testid={`team-load-bar-${row.id}`}
    >
      {scale <= MAX_BLOCKS
        ? blocks.map((kind, index) => (
          // blocks are interchangeable: the index is a fine key
          <span key={index} className={cn('block rounded-[2px]', fill[kind])} style={{ width: `calc(${100 / scale}% - 2px)` }} />
        ))
        : ['progress', 'red', 'open'].map((kind) => {
          const count = kind === 'progress' ? row.progress : kind === 'red' ? row.red : row.plain;
          return count ? <span key={kind} className={cn('block rounded-[2px]', fill[kind])} style={{ width: `${(count / scale) * 100}%` }} /> : null;
        })}
    </span>
  );
}

function Attention({ row }) {
  const parts = [row.late ? `${row.late} late` : '', row.blocked ? `${row.blocked} blocked` : ''].filter(Boolean);
  if (!parts.length) return <span className="text-ink-4" aria-label="Nothing needs attention">—</span>;
  return <span className="font-bold text-status-red-ink">{parts.join(' · ')}</span>;
}

// Two row layouts, picked by the card's own width (not the window: beside the docked task pane the Team view
// is narrow on a wide screen): a table (Person · Load · Open · Attention · Done) or stacked (name + Open N,
// the bar under them, attention and done after the role).
const GRID_TABLE = 'grid grid-cols-[200px_minmax(0,1fr)_64px_118px_64px] items-center gap-x-4';
const GRID_TABLE_FULL = 'grid grid-cols-[200px_minmax(0,1fr)_64px_118px_64px_28px] items-center gap-x-4';
const GRID_STACK = 'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 py-1.5';
const GRID_STACK_FULL = 'grid grid-cols-[minmax(0,1fr)_auto_28px] items-center gap-x-4 gap-y-1.5 py-1.5';
const gridClass = (table, full) => (table ? (full ? GRID_TABLE_FULL : GRID_TABLE) : (full ? GRID_STACK_FULL : GRID_STACK));

/** Width of an element (0 until measured, and in tests — callers treat 0 as "wide"). */
function useElementWidth(ref) {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const measure = () => setWidth(Math.round(element.getBoundingClientRect().width));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

function TaskRows({ tasks, fromPersonId, indent, projectNameById, members, onOpenTask, onAssign, onDragTask, onDragEnd, readOnly = false }) {
  const [pickerFor, setPickerFor] = useState(null);
  return (
    <ul className={cn('mb-2 flex flex-col gap-1', indent && 'ml-[38px]')} aria-label="Open tasks">
      {tasks.map((task) => {
        const projects = taskProjects(task, projectNameById);
        const assigned = taskAssignees(task).map((person) => person.id);
        return (
          <li
            key={task.id}
            draggable={!readOnly}
            onDragStart={readOnly ? undefined : (event) => {
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData('text/plain', task.id);
              onDragTask({ taskId: task.id, fromPersonId });
            }}
            onDragEnd={readOnly ? undefined : onDragEnd}
            className={cn(
              'group flex min-h-[36px] items-center gap-2.5 rounded-lg border border-line bg-surface px-2.5 py-1 shadow-card',
              !readOnly && 'cursor-grab active:cursor-grabbing'
            )}
            data-testid={`team-task-${fromPersonId}-${task.id}`}
          >
            <StatusIcon status={task.status} />
            <button
              type="button"
              onClick={() => onOpenTask(task)}
              className="min-w-0 flex-1 truncate rounded text-left text-[13px] font-semibold text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {task.name}
            </button>
            {projects.length ? (
              <span className="hidden max-w-[180px] truncate rounded-md border border-line bg-surface-2 px-[7px] text-[11.5px] font-semibold leading-[18px] text-ink-2 sm:block">
                {projects[0].name}
              </span>
            ) : null}
            <DueBadge task={task} className="shrink-0 text-[12px]" />
            {readOnly ? null : (
              <Popover open={pickerFor === task.id} onOpenChange={(open) => setPickerFor(open ? task.id : null)}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-md text-ink-3 transition-colors hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`${fromPersonId === UNASSIGNED ? 'Assign' : 'Move'} ${task.name} to…`}
                    title={fromPersonId === UNASSIGNED ? 'Assign to…' : 'Move to another person…'}
                    data-testid={`team-task-assign-${task.id}`}
                  >
                    <UserPlus className="h-[15px] w-[15px]" aria-hidden="true" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={6} className="w-[288px] rounded-xl border-line bg-surface p-0 pb-1 text-ink shadow-overlay">
                  <AssignPanel
                    title={fromPersonId === UNASSIGNED ? 'Assign to' : 'Move to'}
                    members={members}
                    selectedIds={fromPersonId === UNASSIGNED ? [] : assigned}
                    onToggle={(member) => {
                      setPickerFor(null);
                      onAssign(task.id, member, { fromPersonId: fromPersonId === UNASSIGNED ? undefined : fromPersonId });
                    }}
                  />
                </PopoverContent>
              </Popover>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function PersonRow({
  row,
  unassigned = false,
  full,
  table,
  expanded,
  onToggle,
  scale,
  period,
  lastColumn,
  dropActive,
  dropOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onOpenPerson,
  onShowPerson,
  onShowNeedsDetails,
  readOnly = false,
  children,
}) {
  const name = unassigned ? 'Unassigned' : row.name;
  const open = (
    <button
      type="button"
      onClick={() => onShowPerson(unassigned ? UNASSIGNED : row.id)}
      disabled={!row.open}
      className={cn(
        'whitespace-nowrap rounded text-[13px] font-semibold text-ink enabled:hover:text-brand-ink enabled:hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:text-ink-3',
        table ? 'justify-self-start' : 'justify-self-end'
      )}
      title={row.open ? `Show ${unassigned ? 'unassigned tasks' : `${row.name}’s tasks`} on the board` : undefined}
      data-testid={`team-load-open-${row.id || UNASSIGNED}`}
    >
      {period === 'next' ? 'Due' : 'Open'} {row.open}
    </button>
  );
  const needsDetails = unassigned && row.needsDetails ? (
    <button
      type="button"
      onClick={onShowNeedsDetails}
      className="rounded font-bold text-status-amber-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      title="Tasks with no project, person or date wait in Needs details"
    >
      +{row.needsDetails} need details
    </button>
  ) : null;
  const who = (
    <div className="flex min-w-0 items-center gap-2.5">
      {unassigned ? (
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border-[1.5px] border-dashed border-status-amber text-status-amber-ink" aria-hidden="true">
          <UserPlus className="h-3.5 w-3.5" />
        </span>
      ) : (
        <PersonAvatar person={row} size={28} />
      )}
      <div className="min-w-0">
        {unassigned ? (
          <button
            type="button"
            onClick={() => onShowPerson(UNASSIGNED)}
            disabled={!row.open}
            className="block max-w-full truncate rounded text-left text-[13.5px] font-semibold leading-[1.2] text-ink enabled:hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {name}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onOpenPerson(row)}
            className="block max-w-full truncate rounded text-left text-[13.5px] font-semibold leading-[1.2] text-ink hover:text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title={`${row.name} — full task history`}
            data-testid={`team-load-person-${row.id}`}
          >
            {name}
          </button>
        )}
        <div className="truncate text-[12px] leading-[1.25] text-ink-3">
          {unassigned ? 'Waiting for a person' : row.role || 'Team member'}
          {/* stacked: the Done column follows the role */}
          {!table && !unassigned ? <> · {lastColumn(row)}</> : null}
        </div>
        {/* stacked: what needs attention (and Unassigned's "+N need details") get their own line, never cut off */}
        {!table && !unassigned && (row.late || row.blocked) ? (
          <div className="text-[12px] leading-[1.3]" data-testid={`team-load-attention-${row.id}`}><Attention row={row} /></div>
        ) : null}
        {!table && needsDetails ? <div className="text-[12px] leading-[1.3]">{needsDetails}</div> : null}
      </div>
    </div>
  );
  const bar = (
    <div className={cn('min-w-0', !table && (full ? 'col-span-3' : 'col-span-2'))}>
      {unassigned && !readOnly ? (
        <div
          className={cn(
            'flex h-[30px] items-center justify-center gap-1.5 rounded-lg border-[1.5px] border-dashed px-2 text-center text-[12px] font-semibold',
            dropActive ? 'border-[color:var(--brand-soft-border)] text-brand-ink' : 'border-[color:var(--line-dashed)] text-ink-3'
          )}
        >
          <span className="inline-flex min-w-0 items-center gap-1.5 [@media(hover:none)]:hidden">
            <UserPlus className="h-[13px] w-[13px] shrink-0" aria-hidden="true" />
            <span className="truncate">{full ? 'Drag a task onto a person' : 'Drop a card on a person to assign'}</span>
          </span>
          {/* touch screens: the tap path that exists there */}
          <span className="hidden min-w-0 items-center gap-1.5 [@media(hover:none)]:inline-flex" data-testid="team-load-touch-hint">
            {full ? (
              <>Tap <UserPlus className="h-[13px] w-[13px] shrink-0" aria-label="the person icon" /> <span className="truncate">on a task to assign it</span></>
            ) : (
              <><UserPlus className="h-[13px] w-[13px] shrink-0" aria-hidden="true" /><span className="truncate">Tap ⋯ › Assign people… on a card</span></>
            )}
          </span>
        </div>
      ) : dropOver ? (
        <div className="flex h-[30px] items-center justify-center rounded-lg border-[1.5px] border-dashed border-[color:var(--brand-ink)] text-[12px] font-bold text-brand-ink">
          Drop to assign to {row.name}
        </div>
      ) : (
        <LoadBar row={row} scale={scale} />
      )}
    </div>
  );
  const chevron = full ? (
    <div>
      {row.open ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="text-ink-3"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Hide' : 'Show'} ${unassigned ? 'unassigned tasks' : `${row.name}’s tasks`}`}
          data-testid={`team-load-expand-${row.id || UNASSIGNED}`}
        >
          {expanded ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
        </Button>
      ) : null}
    </div>
  ) : null;

  return (
    <li
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        // -mx-2 px-2: room for the drop highlight without shifting the columns
        '-mx-2 border-b border-line-2 px-2 last:border-b-0',
        dropOver && 'rounded-lg border-transparent bg-brand-soft shadow-[inset_0_0_0_1.5px_var(--brand-ink)]'
      )}
      data-testid={`team-load-row-${unassigned ? UNASSIGNED : row.id}`}
      data-drop-over={dropOver ? 'true' : undefined}
    >
      <div className={cn(gridClass(table, full), 'min-h-[46px]')}>
        {table ? (
          <>
            {who}
            {bar}
            {open}
            <div className="text-[13px]">{unassigned ? (needsDetails || <span className="text-ink-4">—</span>) : <Attention row={row} />}</div>
            <div className="text-[13px] text-ink-3">{unassigned ? '—' : lastColumn(row)}</div>
            {chevron}
          </>
        ) : (
          <>
            {who}
            {open}
            {chevron}
            {bar}
          </>
        )}
      </div>
      {full && expanded && row.open ? children : null}
    </li>
  );
}

/* --------------------------------------------------------------------------------------------- the card */

export default function TeamLoad({
  mode = 'card',
  load,
  allOpenById = {},
  weeks,
  weeksLoading = false,
  period = 'this',
  onPeriodChange,
  filtered = false,
  searching = false,
  limit = 5,
  members = [],
  projectNameById = {},
  externalDragTaskId = null,
  onDragIntoTeam,
  onAssign,
  onOpenTask,
  onOpenPerson,
  onShowPerson,
  onShowNeedsDetails,
  onOpenTeamView,
  onBackToBoard,
  readOnly = false,
}) {
  const full = mode === 'full';
  const sectionRef = useRef(null);
  const width = useElementWidth(sectionRef);
  // 0 = not measured (tests, first frame): the wide layout. Card padding 40px, rate column 300 + 28 gap.
  const sideRate = !width || width >= (full ? 980 : 920);
  const table = !width || (width - 40 - (sideRate ? 328 : 0)) >= (full ? 560 : 520);
  const [localDrag, setLocalDrag] = useState(null); // { taskId, fromPersonId } while a team-view task row is dragged
  const [overId, setOverId] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set([UNASSIGNED]));
  const dragging = !readOnly && Boolean(localDrag || externalDragTaskId);

  const people = full ? load.people : load.people.slice(0, limit);
  const hidden = load.people.length - people.length;
  const range = `${shortDate(load.range.start)} – ${shortDate(load.range.end)}`;
  const subtitle = period === 'next'
    ? `Tasks due next week by person · ${range}`
    : 'Open tasks by person · this week';

  const endDrag = () => {
    setLocalDrag(null);
    setOverId(null);
  };
  const dropHandlers = (row) => (readOnly ? {} : {
    onDragOver: (event) => {
      if (!dragging) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (overId !== row.id) {
        setOverId(row.id);
        onDragIntoTeam?.();
      }
    },
    onDragLeave: (event) => {
      if (!event.currentTarget.contains(event.relatedTarget) && overId === row.id) setOverId(null);
    },
    onDrop: (event) => {
      event.preventDefault();
      const taskId = localDrag?.taskId || externalDragTaskId || event.dataTransfer.getData('text/plain');
      const fromPersonId = localDrag?.fromPersonId && localDrag.fromPersonId !== UNASSIGNED ? localDrag.fromPersonId : undefined;
      endDrag();
      if (taskId) onAssign(taskId, row, { fromPersonId });
    },
  });
  const toggle = (id) => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const lastColumn = (row) => (period === 'next'
    ? <span title="Everything open for them, any due date">All {allOpenById[row.id] ?? row.open}</span>
    : `Done ${row.done}`);
  const taskRows = (row, fromPersonId) => (
    <TaskRows
      tasks={row.tasks}
      fromPersonId={fromPersonId}
      indent={table}
      projectNameById={projectNameById}
      members={members}
      onOpenTask={onOpenTask}
      onAssign={onAssign}
      onDragTask={setLocalDrag}
      onDragEnd={endDrag}
      readOnly={readOnly}
    />
  );

  const nobody = !load.people.length && !load.unassigned.open && !load.unassigned.needsDetails;

  return (
    <section
      ref={sectionRef}
      className="rounded-card border border-line bg-surface shadow-card"
      aria-labelledby="tasks-team-load-title"
      data-testid={full ? 'tasks-team-view' : 'tasks-team-load'}
    >
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line-2 px-4 py-3.5 sm:px-5">
        <span className="ui-icon-tile tone-navy h-9 w-9" aria-hidden="true"><Users className="h-[17px] w-[17px]" /></span>
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <h2 id="tasks-team-load-title" className="text-[15px] font-bold text-ink">{full ? 'Team view' : 'Team load'}</h2>
          <span className="text-[12.5px] text-ink-3">
            {subtitle}
            {filtered ? <span data-testid="team-load-filtered"> · filtered like the board</span> : null}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Segmented
            ariaLabel="Team load period"
            size="sm"
            value={period}
            onChange={onPeriodChange}
            options={TEAM_PERIODS.map((item) => ({ key: item.key, label: item.label, testId: `team-load-period-${item.key}` }))}
          />
          {full ? (
            <button
              type="button"
              onClick={onBackToBoard}
              className="inline-flex items-center gap-1 rounded text-[12.5px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="team-load-back"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Back to board
            </button>
          ) : (
            <button
              type="button"
              onClick={onOpenTeamView}
              className="rounded text-[12.5px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="team-load-open-view"
            >
              Open team view
            </button>
          )}
        </div>
      </header>

      <div
        className={cn('grid gap-x-7 px-4 pb-[18px] pt-1.5 sm:px-5', sideRate && 'grid-cols-[minmax(0,1fr)_300px]')}
        data-layout={`${table ? 'table' : 'stacked'}-${sideRate ? 'side' : 'below'}`}
      >
        <div className="min-w-0">
          {nobody ? (
            <p className="py-6 text-center text-[13px] text-ink-3" data-testid="team-load-empty">
              {period === 'next' ? 'Nothing is due next week yet.' : 'No open tasks — nobody is loaded right now.'}
            </p>
          ) : (
            <>
              {table ? (
                <div className={cn(gridClass(true, full), 'h-[34px]')} aria-hidden="true">
                  {['Person', 'Load', period === 'next' ? 'Due' : 'Open', 'Attention', period === 'next' ? 'All open' : 'Done'].map((label) => (
                    <span key={label} className="text-[11px] font-bold uppercase tracking-[0.05em] text-ink-3">{label}</span>
                  ))}
                </div>
              ) : null}
              <ul className="flex flex-col" aria-label="People and their open tasks">
                {people.map((row) => (
                  <PersonRow
                    key={row.id}
                    row={row}
                    full={full}
                    table={table}
                    expanded={expanded.has(row.id)}
                    onToggle={() => toggle(row.id)}
                    scale={load.scale}
                    period={period}
                    lastColumn={lastColumn}
                    dropActive={dragging}
                    dropOver={dragging && overId === row.id}
                    {...dropHandlers(row)}
                    onOpenPerson={onOpenPerson}
                    onShowPerson={onShowPerson}
                    readOnly={readOnly}
                  >
                    {taskRows(row, row.id)}
                  </PersonRow>
                ))}
                {hidden > 0 ? (
                  <li className="-mx-2 border-b border-line-2 px-2">
                    <button
                      type="button"
                      onClick={onOpenTeamView}
                      className="flex h-10 w-full items-center gap-2 rounded text-left text-[12.5px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid="team-load-more"
                    >
                      <Users className="h-3.5 w-3.5" aria-hidden="true" />
                      {plural(hidden, 'more person').replace('persons', 'people')} — open the team view
                    </button>
                  </li>
                ) : null}
                <PersonRow
                  row={{ ...load.unassigned, id: UNASSIGNED }}
                  unassigned
                  full={full}
                  table={table}
                  expanded={expanded.has(UNASSIGNED)}
                  onToggle={() => toggle(UNASSIGNED)}
                  scale={load.scale}
                  period={period}
                  lastColumn={lastColumn}
                  dropActive={dragging}
                  dropOver={false}
                  onOpenPerson={onOpenPerson}
                  onShowPerson={onShowPerson}
                  onShowNeedsDetails={onShowNeedsDetails}
                  readOnly={readOnly}
                >
                  {taskRows(load.unassigned, UNASSIGNED)}
                </PersonRow>
              </ul>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] font-semibold text-ink-3" aria-hidden="true">
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px] bg-status-blue" />In progress</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px] bg-[color:var(--status-gray)]" />Open</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px] bg-status-red" />Blocked / overdue</span>
                <span className="flex-1" />
                {load.scale <= MAX_BLOCKS ? <span>Each block = 1 task</span> : null}
                {readOnly ? null : full ? (
                  <span className="inline-flex items-center gap-1 [@media(hover:none)]:hidden" data-testid="team-load-drag-hint">
                    <Columns3 className="h-3 w-3" />Drag a task onto a person to move it — or use <UserPlus className="h-3 w-3" /> on the task
                  </span>
                ) : (
                  <span className="[@media(hover:none)]:hidden" data-testid="team-load-keyboard-hint">Keyboard: Assign on a focused card</span>
                )}
              </div>
            </>
          )}
        </div>
        <RatePanel weeks={weeks} loading={weeksLoading} side={sideRate} searching={searching} />
      </div>
    </section>
  );
}
